import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface TowersPuzzle {
  size: number;
  difficulty: string;
  grid: number[];
  solution: number[];
  clues: {
    top: number[];
    bottom: number[];
    left: number[];
    right: number[];
  };
  seed: number;
}

// CSS para las torres visuales
const towerStyles = `
  .tower-cell {
    position: relative;
    overflow: hidden;
  }

  /* Torre visual - barra que representa la altura */
  .tower-cell::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 70%;
    background: linear-gradient(to top, #667eea, #764ba2);
    border-radius: 3px 3px 0 0;
    transition: height 0.3s ease, opacity 0.3s ease;
    opacity: 0;
    height: 0;
    z-index: 1;
  }

  /* Número que se muestra sobre la torre */
  .tower-cell::after {
    content: attr(data-value);
    position: absolute;
    bottom: 2px;
    font-size: 0.7em;
    color: white;
    font-weight: bold;
    z-index: 2;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  /* Alturas para cada valor (1-7) */
  .tower-cell[data-value="1"]::before { height: 14%; opacity: 1; }
  .tower-cell[data-value="2"]::before { height: 28%; opacity: 1; }
  .tower-cell[data-value="3"]::before { height: 42%; opacity: 1; }
  .tower-cell[data-value="4"]::before { height: 56%; opacity: 1; }
  .tower-cell[data-value="5"]::before { height: 70%; opacity: 1; }
  .tower-cell[data-value="6"]::before { height: 84%; opacity: 1; }
  .tower-cell[data-value="7"]::before { height: 98%; opacity: 1; }

  /* Mostrar el número sobre la torre */
  .tower-cell[data-value]:not([data-value="0"])::after {
    opacity: 1;
  }

  /* Celdas dadas por el sistema — torre ámbar */
  .tower-cell-given::before {
    background: linear-gradient(to top, #d97706, #b45309) !important;
  }

  /* Ajustar color de la torre para celdas seleccionadas */
  .tower-cell-selected::before {
    background: linear-gradient(to top, #ffd700, #ffa500) !important;
  }
`;

export default function TowersGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : undefined;

  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 20);
      setBoardH(clientHeight - 100); // reservar ~80px para el number picker
    }
  };

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);

  const [puzzle, setPuzzle] = useState<TowersPuzzle | null>(null);
  const [userGrid, setUserGrid] = useState<number[]>([]);
  const [givenCells, setGivenCells] = useState<Set<number>>(new Set());
  const [selectedCell, setSelectedCell] = useState<number | null>(null);
  const [history, setHistory] = useState<number[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [startTime] = useState(Date.now());
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const dificultadOrden = parseInt(dificultad || '1');
  const isFirstLoad = useRef(true);
  const [forceReload, setForceReload] = useState(0);
  const { saveProgress: saveGameProgress } = useGameProgress('torres', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Mapeo de dificultad a tamaño
  const getSizeFromDificultad = (orden: number): number => {
    const sizeMap: Record<number, number> = {
      1: 4,  // Neurona
      2: 5,  // Sinapsis
      3: 5,  // Cerebro (mismo tamaño que sinapsis, menos pistas)
      4: 6,  // Genio
      5: 7,  // Einstein
    };
    return sizeMap[orden] || 5;
  };

  // Cargar puzzle
  useEffect(() => {
    const loadPuzzle = async () => {
      try {
        setLoading(true);
        setError('');

        const size = getSizeFromDificultad(dificultadOrden);
        const isNewGame = forceReload > 0;
        const response = await api.get('/towers/generate', {
          params: { size, ...(!isNewGame && seedParam !== undefined && { seed: seedParam }) }
        });

        const puzzleData: TowersPuzzle = response.data.data;
        setPuzzle(puzzleData);

        // Actualizar URL con seed
        const newParams = new URLSearchParams(searchParams);
        newParams.set('seed', puzzleData.seed.toString());
        setSearchParams(newParams, { replace: true });

        // Medir tablero tras render
        requestAnimationFrame(measureBoard);

        // Inicializar grid de usuario con las celdas dadas
        const initialGrid = [...puzzleData.grid];
        setUserGrid(initialGrid);

        // Marcar celdas dadas (no editables)
        const given = new Set<number>();
        puzzleData.grid.forEach((value, index) => {
          if (value > 0) {
            given.add(index);
          }
        });
        setGivenCells(given);

        // Inicializar historial
        setHistory([JSON.parse(JSON.stringify(initialGrid))]);
        setHistoryIndex(0);
        setIsCompleted(false);
        setShowVictory(false);
        setSelectedCell(null);
      } catch (err: any) {
        console.error('Error al cargar puzzle:', err);
        setError(err.response?.data?.error || 'Error al cargar el puzzle');
      } finally {
        setLoading(false);
        isFirstLoad.current = false;
      }
    };

    loadPuzzle();
  }, [dificultadOrden, forceReload]);

  // Guardar estado en historial
  const saveState = (newState: number[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newState)));

    if (newHistory.length > 50) {
      newHistory.shift();
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    } else {
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  };

  // Manejar teclas
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!puzzle || selectedCell === null || givenCells.has(selectedCell)) return;

      const size = puzzle.size;
      const row = Math.floor(selectedCell / size);
      const col = selectedCell % size;

      // Números del 1 al tamaño
      if (e.key >= '1' && e.key <= '9') {
        const num = parseInt(e.key);
        if (num <= size) {
          setCellValue(selectedCell, num);
        }
      }
      // Borrar
      else if (e.key === '0' || e.key === 'Backspace' || e.key === 'Delete' || e.key === ' ') {
        e.preventDefault();
        setCellValue(selectedCell, 0);
      }
      // Navegación con flechas
      else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        let newRow = row;
        let newCol = col;

        switch (e.key) {
          case 'ArrowUp':
            newRow = Math.max(0, row - 1);
            break;
          case 'ArrowDown':
            newRow = Math.min(size - 1, row + 1);
            break;
          case 'ArrowLeft':
            newCol = Math.max(0, col - 1);
            break;
          case 'ArrowRight':
            newCol = Math.min(size - 1, col + 1);
            break;
        }

        const newIndex = newRow * size + newCol;
        if (newIndex !== selectedCell) {
          setSelectedCell(newIndex);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [puzzle, selectedCell, givenCells]);

  // Establecer valor de celda
  const setCellValue = (index: number, value: number) => {
    if (givenCells.has(index) || !puzzle) return;

    const newGrid = [...userGrid];
    newGrid[index] = value;
    setUserGrid(newGrid);
    saveState(newGrid);

    // Auto-verificar si está completo
    setTimeout(() => autoCheckSolution(newGrid), 100);
  };

  // Auto-verificar solución
  const autoCheckSolution = (grid: number[]) => {
    if (!puzzle) return;

    // Solo verificar si está completo
    const isComplete = !grid.includes(0);
    if (!isComplete) return;

    // Verificar que sea correcto comparando con la solución
    const isCorrect = grid.every((val, idx) => val === puzzle.solution[idx]);

    if (isCorrect && !isCompleted) {
      setIsCompleted(true);
      const tiempo = Math.floor((Date.now() - startTime) / 1000);
      saveProgress(tiempo);
      setShowVictory(true);
    }
  };

  // Deshacer
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setUserGrid(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  };

  // Rehacer
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setUserGrid(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  };

  // Pista
  const handleHint = () => {
    if (!puzzle) return;

    // Encontrar primera celda incorrecta o vacía
    for (let i = 0; i < userGrid.length; i++) {
      if (!givenCells.has(i) && userGrid[i] !== puzzle.solution[i]) {
        const newGrid = [...userGrid];
        newGrid[i] = puzzle.solution[i];
        setUserGrid(newGrid);
        saveState(newGrid);
        setGivenCells(new Set([...givenCells, i]));
        setError('💡 Pista añadida');
        setTimeout(() => setError(''), 2000);
        return;
      }
    }

    setError('¡El puzzle ya está resuelto correctamente!');
    setTimeout(() => setError(''), 2000);
  };

  // Mostrar solución
  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Estás seguro? Esto mostrará la solución completa.')) return;

    setUserGrid([...puzzle.solution]);
    setHistory([[...puzzle.solution]]);
    setHistoryIndex(0);
    setError('Solución mostrada');
    setTimeout(() => setError(''), 3000);
  };

  // Reiniciar
  const handleReset = () => {
    if (!puzzle || !window.confirm('¿Estás seguro de que quieres reiniciar el tablero?')) return;

    const resetGrid = [...puzzle.grid];
    setUserGrid(resetGrid);
    setHistory([JSON.parse(JSON.stringify(resetGrid))]);
    setHistoryIndex(0);
    setIsCompleted(false);
    setShowVictory(false);
    setSelectedCell(null);
    setError('');

    // Restaurar solo las celdas dadas originales
    const given = new Set<number>();
    puzzle.grid.forEach((value, index) => {
      if (value > 0) {
        given.add(index);
      }
    });
    setGivenCells(given);
  };

  // Guardar progreso
  const saveProgress = async (tiempo: number) => {
    if (!puzzle) return;

    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: puzzle.seed,
        tiempo,
        movimientos: 0,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos: 0,
        completado: true,
        datos_juego: {
          tablero_id: puzzle.seed,
          size: puzzle.size,
        },
      });
    } catch (error) {
      console.error('Error al guardar progreso:', error);
    }
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(grid: number[], puz: TowersPuzzle): { cells: Set<number>; clues: Set<string> } {
    const { size, clues } = puz;
    const errorCells = new Set<number>();
    const errorClues = new Set<string>();

    // 1. Duplicados en fila
    for (let r = 0; r < size; r++) {
      const seen = new Map<number, number>();
      for (let c = 0; c < size; c++) {
        const v = grid[r * size + c];
        if (!v) continue;
        if (seen.has(v)) {
          errorCells.add(r * size + seen.get(v)!);
          errorCells.add(r * size + c);
        } else seen.set(v, c);
      }
    }

    // 2. Duplicados en columna
    for (let c = 0; c < size; c++) {
      const seen = new Map<number, number>();
      for (let r = 0; r < size; r++) {
        const v = grid[r * size + c];
        if (!v) continue;
        if (seen.has(v)) {
          errorCells.add(seen.get(v)! * size + c);
          errorCells.add(r * size + c);
        } else seen.set(v, r);
      }
    }

    // 3. Visibilidad en filas/columnas completas
    const countVisible = (line: number[]): number => {
      let visible = 0, maxH = 0;
      for (const h of line) { if (h > maxH) { visible++; maxH = h; } }
      return visible;
    };

    for (let r = 0; r < size; r++) {
      const row = Array.from({ length: size }, (_, c) => grid[r * size + c]);
      if (row.includes(0)) continue;
      if (clues.left[r] > 0 && countVisible(row) !== clues.left[r]) {
        errorClues.add(`left-${r}`);
        row.forEach((_, c) => errorCells.add(r * size + c));
      }
      if (clues.right[r] > 0 && countVisible([...row].reverse()) !== clues.right[r]) {
        errorClues.add(`right-${r}`);
        row.forEach((_, c) => errorCells.add(r * size + c));
      }
    }

    for (let c = 0; c < size; c++) {
      const col = Array.from({ length: size }, (_, r) => grid[r * size + c]);
      if (col.includes(0)) continue;
      if (clues.top[c] > 0 && countVisible(col) !== clues.top[c]) {
        errorClues.add(`top-${c}`);
        col.forEach((_, r) => errorCells.add(r * size + c));
      }
      if (clues.bottom[c] > 0 && countVisible([...col].reverse()) !== clues.bottom[c]) {
        errorClues.add(`bottom-${c}`);
        col.forEach((_, r) => errorCells.add(r * size + c));
      }
    }

    return { cells: errorCells, clues: errorClues };
  }

  // +2 por las pistas en los 4 lados
  const totalCols = puzzle ? puzzle.size + 2 : 8;
  const cellMarginOverhead = totalCols * 4; // mx-0.5 my-0.5 = 2px cada lado
  const cellW = boardW > 0 ? Math.floor((boardW - cellMarginOverhead) / totalCols) : 50;
  const cellH = boardH > 0 ? Math.floor((boardH - cellMarginOverhead) / totalCols) : 50;
  const padEnabled = selectedCell !== null && !givenCells.has(selectedCell);
  const cellWpx = `${cellW}px`;
  const cellHpx = `${cellH}px`;
  const fontSize = `${Math.floor(Math.min(cellW, cellH) * 0.5)}px`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">❌ Error al cargar puzzle</div>
      </div>
    );
  }

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  return (
    <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col">
      {/* Inyectar estilos CSS para las torres */}
      <style>{towerStyles}</style>

      {/* Banner de Sesión */}
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🏢 Torres</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (dificultadOrden === d.orden) setForceReload(prev => prev + 1);
                else navigate(`/juegos/torres/${d.orden}`);
              }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dificultadOrden === d.orden ? 'bg-white text-purple-700' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          <button onClick={handleUndo} disabled={historyIndex <= 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          <button onClick={handleRedo} disabled={historyIndex >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          <button onClick={handleShowSolution} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={() => setForceReload(prev => prev + 1)} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      {/* Board area */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex flex-col items-center justify-center p-2 pb-safe gap-2 overflow-hidden">
        {(() => {
          const { cells: errorCells, clues: errorClues } = modoAsistido
            ? calcularErrores(userGrid, puzzle)
            : { cells: new Set<number>(), clues: new Set<string>() };

          return (
        <div className="inline-block">
          {/* Top clues */}
          <div className="flex items-center justify-center mb-1">
            <div style={{ width: cellWpx, height: cellHpx }} />
            {puzzle.clues.top.map((clue, c) => (
              <div key={`top-${c}`}
                className={`flex items-center justify-center font-bold rounded mx-0.5 ${errorClues.has(`top-${c}`) ? 'text-red-400' : 'text-purple-300'}`}
                style={{ width: cellWpx, height: cellHpx, fontSize: fontSize }}
              >
                {clue > 0 ? clue : ''}
              </div>
            ))}
            <div style={{ width: cellWpx, height: cellHpx }} />
          </div>

          {/* Board rows with lateral clues */}
          {Array.from({ length: puzzle.size }, (_, r) => (
            <div key={`row-${r}`} className="flex items-center justify-center">
              <div className={`flex items-center justify-center font-bold rounded mr-1 ${errorClues.has(`left-${r}`) ? 'text-red-400' : 'text-purple-300'}`}
                style={{ width: cellWpx, height: cellHpx, fontSize: fontSize }}
              >
                {puzzle.clues.left[r] > 0 ? puzzle.clues.left[r] : ''}
              </div>

              {Array.from({ length: puzzle.size }, (_, c) => {
                const index = r * puzzle.size + c;
                const isGiven = givenCells.has(index);
                const value = userGrid[index];
                const isSelected = selectedCell === index;
                const hasError = errorCells.has(index);

                const bgColor = hasError ? '#fecaca' : isGiven ? '#fde68a' : isSelected ? '#bfdbfe' : value > 0 ? '#bbf7d0' : '#ffffff';
                const borderColor = hasError ? '#f87171' : isGiven ? '#b45309' : isSelected ? '#3b82f6' : value > 0 ? '#86efac' : '#d1d5db';
                return (
                  <div
                    key={`${r}-${c}`}
                    onClick={() => !isGiven && setSelectedCell(index)}
                    data-value={value || '0'}
                    className={`tower-cell flex items-center justify-center rounded transition-all mx-0.5 my-0.5 border-2 ${isGiven ? 'tower-cell-given' : ''} ${isSelected && !hasError ? 'tower-cell-selected' : ''}`}
                    style={{ width: cellWpx, height: cellHpx, fontSize, backgroundColor: bgColor, borderColor, cursor: isGiven ? 'default' : 'pointer', fontWeight: isGiven ? 'bold' : undefined }}
                  />
                );
              })}

              <div className={`flex items-center justify-center font-bold rounded ml-1 ${errorClues.has(`right-${r}`) ? 'text-red-400' : 'text-purple-300'}`}
                style={{ width: cellWpx, height: cellHpx, fontSize: fontSize }}
              >
                {puzzle.clues.right[r] > 0 ? puzzle.clues.right[r] : ''}
              </div>
            </div>
          ))}

          {/* Bottom clues */}
          <div className="flex items-center justify-center mt-1">
            <div style={{ width: cellWpx, height: cellHpx }} />
            {puzzle.clues.bottom.map((clue, c) => (
              <div key={`bottom-${c}`}
                className={`flex items-center justify-center font-bold rounded mx-0.5 ${errorClues.has(`bottom-${c}`) ? 'text-red-400' : 'text-purple-300'}`}
                style={{ width: cellWpx, height: cellHpx, fontSize: fontSize }}
              >
                {clue > 0 ? clue : ''}
              </div>
            ))}
            <div style={{ width: cellWpx, height: cellHpx }} />
          </div>
        </div>
          );
        })()}

        {/* Number picker (touch-friendly). Siempre presente: el hueco ya esta
            reservado en measureBoard, pero si el picker apareciera y
            desapareciera el grupo se recentraria y el tablero se moveria. */}
        <div className={`flex gap-2 flex-wrap justify-center transition-opacity ${padEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
          {Array.from({ length: puzzle.size }, (_, i) => i + 1).map(num => (
            <button key={num} onClick={() => { if (padEnabled) setCellValue(selectedCell!, num); }} disabled={!padEnabled}
              className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-bold rounded-lg shadow active:scale-95 transition-all text-xl">
              {num}
            </button>
          ))}
          <button onClick={() => { if (padEnabled) setCellValue(selectedCell!, 0); }} disabled={!padEnabled} title="Borrar"
            className="w-12 h-12 ml-2 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg shadow active:scale-95 transition-all text-lg">
            ✕
          </button>
        </div>
      </div>

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Torres"
        gameIcon="🏯"
        objective="Coloca números en el tablero de forma que los valores de visibilidad en los bordes sean correctos."
        rules={[
          'Cada fila y columna debe contener exactamente una vez cada número del 1 al N',
          'Los números representan torres de diferentes alturas: 1 es la más baja, N la más alta',
          'Una torre más alta tapa a todas las más bajas que hay detrás de ella',
          'Los números en los bordes indican cuántas torres son visibles desde ese lado',
        ]}
        howToPlay="Toca una celda para seleccionarla y usa el teclado o el selector numérico de la parte inferior para colocar un número."
        author="Nikoli"
      />

      {/* Modal de Victoria */}
      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: 0,
        }}
      />
    </div>
  );
}
